import * as Sentry from "@sentry/react";
import { Breadcrumb, User } from "@sentry/react";

const dsn = import.meta.env.VITE_SENTRY_DSN;
const release = import.meta.env.VITE_SENTRY_RELEASE || `cash-isle-web@${import.meta.env.PACKAGE_VERSION || "0.0.0"}`;

export function initSentry() {
  if (!dsn) {
    console.info("Sentry DSN not configured — error reporting disabled.");
    return;
  }

  Sentry.init({
    dsn,
    release,
    environment: import.meta.env.MODE,
    // Performance
    tracesSampleRate: 0.1,
    // Session replay
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Scrub PII from errors
    beforeSend(event) {
      // Remove URL query params that might contain tokens
      if (event.request?.url) {
        try {
          const url = new URL(event.request.url);
          url.search = "";
          event.request.url = url.toString();
        } catch {
          /* ignore malformed URLs */
        }
      }
      return event;
    },
  });
}

/** Identify the current user in Sentry. Call after login. */
export function setSentryUser(user: User | null) {
  if (!dsn) return;
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email ?? undefined,
    });
  } else {
    Sentry.setUser(null);
  }
}

/** Add a breadcrumb for navigation or user actions. */
export function addBreadcrumb(breadcrumb: Breadcrumb) {
  if (!dsn) return;
  Sentry.addBreadcrumb(breadcrumb);
}

/** Manually capture an exception with optional context. */
export function captureException(error: unknown, context?: Record<string, unknown>) {
  if (!dsn) return;
  Sentry.captureException(error, { extra: context });
}

/** Manually capture a message. */
export function captureMessage(message: string, level?: "fatal" | "error" | "warning" | "log" | "info" | "debug") {
  if (!dsn) return;
  Sentry.captureMessage(message, level);
}

export { Sentry };
